import type {PropertyDefinition} from '@github-ui/custom-properties-types'

export interface DefinitionIceBreakerProps {
  description: string
  suggestedDefinition: Omit<PropertyDefinition, 'source'>
}

export const suggestions: DefinitionIceBreakerProps[] = [
  {
    description: 'Is this a production repository?',
    suggestedDefinition: {
      propertyName: 'deploys_to_production',
      valueType: 'true_false',
      required: true,
      defaultValue: 'false',
      description:
        'Indicates whether the repository deploys to a production environment, or rather a staging or sandbox environment, for example.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Who owns this repository?',
    suggestedDefinition: {
      propertyName: 'owner',
      valueType: 'string',
      required: false,
      defaultValue: null,
      description: 'The user or team responsible for this repository.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Is this repository maintained?',
    suggestedDefinition: {
      propertyName: 'active',
      valueType: 'true_false',
      required: true,
      defaultValue: 'true',
      description: 'Indicates whether the repository is currently active and maintained.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Are regular backups required?',
    suggestedDefinition: {
      propertyName: 'backup_required',
      valueType: 'true_false',
      required: true,
      defaultValue: 'false',
      description: 'Indicates whether the repository requires regular backups.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'How protected is the default branch?',
    suggestedDefinition: {
      propertyName: 'branch_protection_level',
      valueType: 'single_select',
      required: true,
      defaultValue: 'none',
      description: "Defines the strictness of branch protection applied to the repository's default branch.",
      regex: null,
      allowedValues: ['none', 'basic', 'standard', 'strict'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'How sensitive is this data?',
    suggestedDefinition: {
      propertyName: 'data_sensitivity',
      valueType: 'single_select',
      required: true,
      defaultValue: 'internal',
      description: 'Indicates the sensitivity level of the data contained in the repository.',
      regex: null,
      allowedValues: ['public', 'internal', 'confidential', 'restricted'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Are personal identifiers present?',
    suggestedDefinition: {
      propertyName: 'contains_pii',
      valueType: 'true_false',
      required: true,
      defaultValue: 'false',
      description:
        'Indicates whether the data contained in the repository includes personally identifiable information.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'How long until data is deleted?',
    suggestedDefinition: {
      propertyName: 'data_retention_period',
      valueType: 'single_select',
      required: true,
      defaultValue: 'none',
      description: 'Indicates the retention period for the user data contained in the repository.',
      regex: null,
      allowedValues: ['none', '1_year', '3_years', '7_years', 'indefinite'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'What application does this support?',
    suggestedDefinition: {
      propertyName: 'application_name',
      valueType: 'string',
      required: false,
      defaultValue: null,
      description: 'Specifies the name of the application or system this repository supports.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Is this repository open source?',
    suggestedDefinition: {
      propertyName: 'open_source',
      valueType: 'true_false',
      required: true,
      defaultValue: 'false',
      description: 'Indicates whether the repository is open source.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Which CI does this repository use?',
    suggestedDefinition: {
      propertyName: 'ci',
      valueType: 'single_select',
      required: true,
      defaultValue: 'none',
      description: 'Specifies the continuous integration system used by the repository.',
      regex: null,
      allowedValues: ['github_actions', 'jenkins', 'circleci', 'travis_ci', 'azure_devops', 'none'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Which databases are used?',
    suggestedDefinition: {
      propertyName: 'databases',
      valueType: 'multi_select',
      required: false,
      defaultValue: null,
      description: 'Specifies the databases used by the repository.',
      regex: null,
      allowedValues: ['mysql', 'postgresql', 'mongodb', 'redis', 'sqlite'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Which cloud providers are used?',
    suggestedDefinition: {
      propertyName: 'cloud_providers',
      valueType: 'multi_select',
      required: false,
      defaultValue: null,
      description: 'Specifies the cloud providers used by the repository.',
      regex: null,
      allowedValues: ['aws', 'azure', 'google_cloud', 'ibm_cloud', 'oracle_cloud'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Which compliance frameworks apply?',
    suggestedDefinition: {
      propertyName: 'compliance_frameworks',
      valueType: 'multi_select',
      required: false,
      defaultValue: null,
      description: 'Specifies the compliance frameworks applicable to the repository.',
      regex: null,
      allowedValues: ['ISO 27001', 'SOC 2', 'PCI-DSS', 'SOX', 'GDPR', 'HIPAA', 'FedRAMP'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Which runtime environment is used?',
    suggestedDefinition: {
      propertyName: 'runtime_environment',
      valueType: 'single_select',
      required: false,
      defaultValue: null,
      description: 'Specifies the runtime environment which the code in this repository runs in.',
      regex: null,
      allowedValues: ['.net', 'node', 'java', 'python', 'ruby', 'go', 'php'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Are external packages used here?',
    suggestedDefinition: {
      propertyName: 'uses_external_packages',
      valueType: 'true_false',
      required: true,
      defaultValue: 'false',
      description: 'Specifies whether the repository uses external packages, such as libraries or frameworks.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Was this repository migrated?',
    suggestedDefinition: {
      propertyName: 'migration_source',
      valueType: 'single_select',
      required: true,
      defaultValue: 'none',
      description: 'Identifies the platform the repository was migrated from, if any.',
      regex: null,
      allowedValues: ['azure_devops', 'bitbucket', 'gitlab', 'jenkins', 'bamboo', 'other', 'none'],
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
  {
    description: 'Is this repository a monorepo?',
    suggestedDefinition: {
      propertyName: 'monorepo',
      valueType: 'true_false',
      required: false,
      defaultValue: null,
      description:
        'Indicates whether the repository is a monorepo, containing multiple distinct services, packages, or projects rather than a single application.',
      regex: null,
      allowedValues: null,
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
    },
  },
]

export function getShuffledSuggestions(): DefinitionIceBreakerProps[] {
  const shuffled = [...suggestions]

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ;[shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!]
  }

  return shuffled
}
